import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { 
    ArrowLeft, Users, UserPlus, Calendar, Mail, Phone,
    Heart, Edit2, Trash2, Send, Clock, CheckCircle2,
    XCircle, Eye, CalendarDays, UtensilsCrossed, MapPin,
    Link2, Copy, RefreshCw, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCESS_LEVELS = [
    { value: 'view_only', label: 'View Only', description: 'Can see events and menus' },
    { value: 'booking', label: 'Booking', description: 'Can book dining and events as your guest' },
    { value: 'full', label: 'Full Access', description: 'Full access to your bookings' }
];

const RELATIONSHIPS = [
    'Spouse', 'Partner', 'Son', 'Daughter', 'Brother', 'Sister',
    'Grandchild', 'Friend', 'Carer', 'Other'
];

const INVITATION_TYPES = [
    { value: 'visit', label: 'General Visit', icon: Users },
    { value: 'dining', label: 'Dining', icon: UtensilsCrossed },
    { value: 'event', label: 'Event/Activity', icon: CalendarDays }
];

export function FamilyGuestsPage() {
    const navigate = useNavigate();
    const { token } = useAuth();
    const { organization } = useOrganization();
    const [activeTab, setActiveTab] = useState('guests');
    const [loading, setLoading] = useState(true);
    
    // Data
    const [guests, setGuests] = useState([]);
    const [invitations, setInvitations] = useState([]);
    
    // Modals
    const [guestModal, setGuestModal] = useState({ open: false, data: null });
    const [inviteModal, setInviteModal] = useState({ open: false, data: null });
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, guest: null });
    const [shareModal, setShareModal] = useState({ open: false, guest: null });
    const [sendingEmail, setSendingEmail] = useState(false);

    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [guestsRes, invitationsRes] = await Promise.all([
                axios.get(`${API}/family/guests`, { headers }),
                axios.get(`${API}/family/invitations`, { headers })
            ]);
            setGuests(guestsRes.data);
            setInvitations(invitationsRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load family & guests');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveGuest = async () => {
        const data = guestModal.data;
        if (!data.name || !data.email) {
            toast.error('Please fill in name and email');
            return;
        }

        const isNew = !data.id;
        try {
            if (isNew) {
                await axios.post(`${API}/family/guests`, data, { headers });
                toast.success('Guest added successfully');
            } else {
                await axios.put(`${API}/family/guests/${data.id}`, data, { headers });
                toast.success('Guest updated successfully');
            }
            setGuestModal({ open: false, data: null });
            fetchData();
        } catch (error) {
            toast.error('Failed to save guest');
        }
    };

    const handleDeleteGuest = async () => {
        if (!deleteConfirm.guest) return;
        try {
            await axios.delete(`${API}/family/guests/${deleteConfirm.guest.id}`, { headers });
            toast.success('Guest removed');
            setDeleteConfirm({ open: false, guest: null });
            fetchData();
        } catch (error) {
            toast.error('Failed to remove guest');
        }
    };

    const getPortalLink = (accessCode) => {
        return `${window.location.origin}/guest-portal/${accessCode}`;
    };

    const copyPortalLink = (guest) => {
        const link = getPortalLink(guest.access_code);
        navigator.clipboard.writeText(link);
        toast.success('Portal link copied to clipboard!');
    };

    const sendPortalEmail = async (guest) => {
        if (!guest.email) {
            toast.error('Guest has no email address');
            return;
        }
        setSendingEmail(true);
        try {
            await axios.post(`${API}/family/guests/${guest.id}/send-portal-link`, {}, { headers });
            toast.success(`Portal link sent to ${guest.email}`);
            setShareModal({ open: false, guest: null });
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to send email');
        } finally {
            setSendingEmail(false);
        }
    };

    const handleSendInvitation = async () => {
        const data = inviteModal.data;
        if (!data.guest_name || !data.date) {
            toast.error('Please fill in guest name and date');
            return;
        }

        try {
            await axios.post(`${API}/family/invitations`, data, { headers });
            toast.success('Invitation sent');
            setInviteModal({ open: false, data: null });
            fetchData();
        } catch (error) {
            toast.error('Failed to send invitation');
        }
    };

    const openNewGuestModal = () => {
        setGuestModal({
            open: true,
            data: {
                name: '',
                email: '',
                phone: '',
                relationship: 'Family',
                access_level: 'view_only'
            }
        });
    };

    const openNewInviteModal = (guest = null) => {
        setInviteModal({
            open: true,
            data: {
                guest_id: guest?.id || null,
                guest_name: guest?.name || '',
                guest_email: guest?.email || '',
                event_type: 'visit',
                date: '',
                notes: ''
            }
        });
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: 'bg-amber-100 text-amber-700',
            confirmed: 'bg-green-100 text-green-700',
            declined: 'bg-red-100 text-red-700',
            attended: 'bg-blue-100 text-blue-700'
        };
        return colors[status] || 'bg-gray-100 text-gray-600';
    };

    const getAccessLevelColor = (level) => {
        const colors = {
            view_only: 'bg-gray-100 text-gray-600',
            booking: 'bg-blue-100 text-blue-700',
            full: 'bg-purple-100 text-purple-700'
        };
        return colors[level] || 'bg-gray-100 text-gray-600';
    };

    const getEventTypeIcon = (type) => {
        const icons = {
            visit: Users,
            dining: UtensilsCrossed,
            event: CalendarDays
        };
        const Icon = icons[type] || Users;
        return <Icon className="w-4 h-4" />;
    };

    return (
        <div className="min-h-screen bg-stone-50 pb-24" data-testid="family-guests-page">
            {/* Header */}
            <div className="bg-gradient-to-br from-rose-700 to-rose-800 text-white px-6 pt-6 pb-8">
                <button 
                    onClick={() => navigate('/home')}
                    className="flex items-center gap-2 text-rose-200 hover:text-white mb-4"
                >
                    <ArrowLeft className="w-5 h-5" />
                    Home
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                        <Heart className="w-6 h-6" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-serif text-white">Family & Guests</h1>
                        <p className="text-white/80 text-sm">Manage visitors and invitations</p>
                    </div>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="px-4 -mt-4 relative z-10">
                <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-1 flex">
                    <button
                        onClick={() => setActiveTab('guests')}
                        className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                            activeTab === 'guests'
                                ? 'bg-rose-600 text-white'
                                : 'text-stone-600 hover:bg-stone-100'
                        }`}
                        data-testid="tab-guests"
                    >
                        <Users className="w-4 h-4" />
                        My Guests
                    </button>
                    <button
                        onClick={() => setActiveTab('invitations')}
                        className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                            activeTab === 'invitations'
                                ? 'bg-rose-600 text-white'
                                : 'text-stone-600 hover:bg-stone-100'
                        }`}
                        data-testid="tab-invitations"
                    >
                        <Send className="w-4 h-4" />
                        Invitations
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="px-4 mt-6">
                {loading ? (
                    <div className="flex justify-center py-12">
                        <div className="w-8 h-8 border-4 border-rose-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : (
                    <>
                        {/* Guests Tab */}
                        {activeTab === 'guests' && (
                            <div className="space-y-4">
                                <div className="flex justify-between items-center">
                                    <h2 className="text-lg font-semibold text-stone-800">
                                        Registered Guests ({guests.length})
                                    </h2>
                                    <Button 
                                        onClick={openNewGuestModal}
                                        className="bg-rose-600 hover:bg-rose-700"
                                        data-testid="add-guest-btn"
                                    >
                                        <UserPlus className="w-4 h-4 mr-2" />
                                        Add Guest
                                    </Button>
                                </div>

                                {guests.length === 0 ? (
                                    <div className="bg-white rounded-2xl p-8 text-center border border-stone-200">
                                        <Users className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                                        <h3 className="font-semibold text-stone-700 mb-2">No Guests Registered</h3>
                                        <p className="text-stone-500 text-sm mb-4">
                                            Add family members or friends to easily invite them to visit or events.
                                        </p>
                                        <Button onClick={openNewGuestModal} className="bg-rose-600 hover:bg-rose-700">
                                            <UserPlus className="w-4 h-4 mr-2" />
                                            Add Your First Guest
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {guests.map((guest) => (
                                            <div 
                                                key={guest.id}
                                                className="bg-white rounded-2xl p-4 border border-stone-200"
                                                data-testid={`guest-card-${guest.id}`}
                                            >
                                                <div className="flex items-start justify-between">
                                                    <div className="flex items-start gap-4">
                                                        <div className="w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                                                            <span className="text-lg font-semibold text-rose-600">
                                                                {guest.name?.charAt(0)?.toUpperCase()}
                                                            </span>
                                                        </div>
                                                        <div>
                                                            <h3 className="font-semibold text-stone-800">{guest.name}</h3>
                                                            <p className="text-sm text-stone-500">{guest.relationship}</p>
                                                            <div className="flex items-center gap-3 mt-2 text-sm text-stone-500">
                                                                {guest.email && (
                                                                    <span className="flex items-center gap-1">
                                                                        <Mail className="w-3 h-3" />
                                                                        {guest.email}
                                                                    </span>
                                                                )}
                                                                {guest.phone && (
                                                                    <span className="flex items-center gap-1">
                                                                        <Phone className="w-3 h-3" />
                                                                        {guest.phone}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="flex items-center gap-2 mt-2">
                                                                <span className={`text-xs px-2 py-1 rounded-full ${getAccessLevelColor(guest.access_level)}`}>
                                                                    {ACCESS_LEVELS.find(l => l.value === guest.access_level)?.label || 'View Only'}
                                                                </span>
                                                                {guest.access_code && (
                                                                    <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 flex items-center gap-1">
                                                                        <Link2 className="w-3 h-3" />
                                                                        Portal Active
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col gap-2">
                                                        <div className="flex gap-2">
                                                            <Button 
                                                                variant="outline" 
                                                                size="sm"
                                                                className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                                                                onClick={() => setShareModal({ open: true, guest })}
                                                                data-testid={`share-portal-${guest.id}`}
                                                            >
                                                                <ExternalLink className="w-4 h-4" />
                                                            </Button>
                                                            <Button 
                                                                variant="outline" 
                                                                size="sm"
                                                                onClick={() => openNewInviteModal(guest)}
                                                                data-testid={`invite-guest-${guest.id}`}
                                                            >
                                                                <Send className="w-4 h-4" />
                                                            </Button>
                                                        </div>
                                                        <div className="flex gap-2">
                                                            <Button 
                                                                variant="outline" 
                                                                size="sm"
                                                                onClick={() => setGuestModal({ open: true, data: guest })}
                                                                data-testid={`edit-guest-${guest.id}`}
                                                            >
                                                                <Edit2 className="w-4 h-4" />
                                                            </Button>
                                                            <Button 
                                                                variant="outline" 
                                                                size="sm"
                                                                className="text-red-600 hover:text-red-700"
                                                                onClick={() => setDeleteConfirm({ open: true, guest })}
                                                                data-testid={`delete-guest-${guest.id}`}
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Info Card */}
                                <div className="bg-gradient-to-r from-rose-700 to-rose-800 rounded-2xl p-5 mt-6">
                                    <h3 className="font-semibold text-white mb-2">About Guest Access</h3>
                                    <ul className="text-sm text-rose-100 space-y-2">
                                        <li className="flex items-start gap-2">
                                            <Link2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                            <span><strong>Portal Link:</strong> Share a unique link so family can view your activities</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <Eye className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                            <span><strong>View Only:</strong> Can see events, menus, and amenities</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <Calendar className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                            <span><strong>Booking:</strong> Can book dining and events as your guest</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                            <span><strong>Full Access:</strong> Full access to view and manage your bookings</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        )}

                        {/* Invitations Tab */}
                        {activeTab === 'invitations' && (
                            <div className="space-y-4">
                                <div className="flex justify-between items-center">
                                    <h2 className="text-lg font-semibold text-stone-800">
                                        Invitations ({invitations.length})
                                    </h2>
                                    <Button 
                                        onClick={() => openNewInviteModal()}
                                        className="bg-rose-600 hover:bg-rose-700"
                                        data-testid="send-invite-btn"
                                    >
                                        <Send className="w-4 h-4 mr-2" />
                                        Send Invitation
                                    </Button>
                                </div>

                                {invitations.length === 0 ? (
                                    <div className="bg-white rounded-2xl p-8 text-center border border-stone-200">
                                        <Send className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                                        <h3 className="font-semibold text-stone-700 mb-2">No Invitations Yet</h3>
                                        <p className="text-stone-500 text-sm mb-4">
                                            Send invitations to family and friends for visits, dining, or events.
                                        </p>
                                        <Button onClick={() => openNewInviteModal()} className="bg-rose-600 hover:bg-rose-700">
                                            <Send className="w-4 h-4 mr-2" />
                                            Send First Invitation
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {invitations.map((invite) => (
                                            <div 
                                                key={invite.id}
                                                className="bg-white rounded-2xl p-4 border border-stone-200"
                                                data-testid={`invitation-card-${invite.id}`}
                                            >
                                                <div className="flex items-start gap-4">
                                                    <div className="w-10 h-10 rounded-xl bg-stone-100 flex items-center justify-center flex-shrink-0">
                                                        {getEventTypeIcon(invite.event_type)}
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <h3 className="font-semibold text-stone-800">{invite.guest_name}</h3>
                                                            <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${getStatusColor(invite.status)}`}>
                                                                {invite.status}
                                                            </span>
                                                        </div>
                                                        <p className="text-sm text-stone-500 mt-1 capitalize">
                                                            {invite.event_type === 'visit' ? 'General Visit' : invite.event_type}
                                                        </p>
                                                        <div className="flex items-center gap-4 mt-2 text-sm text-stone-500">
                                                            <span className="flex items-center gap-1">
                                                                <Calendar className="w-4 h-4" />
                                                                {formatDate(invite.date)}
                                                            </span>
                                                        </div>
                                                        {invite.notes && (
                                                            <p className="text-sm text-stone-400 mt-2 italic">"{invite.notes}"</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Add/Edit Guest Modal */}
            <Dialog open={guestModal.open} onOpenChange={(open) => !open && setGuestModal({ open: false, data: null })}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>{guestModal.data?.id ? 'Edit Guest' : 'Add Guest'}</DialogTitle>
                    </DialogHeader>
                    {guestModal.data && (
                        <div className="space-y-4">
                            <div>
                                <Label>Name *</Label>
                                <Input
                                    value={guestModal.data.name}
                                    onChange={(e) => setGuestModal({ ...guestModal, data: { ...guestModal.data, name: e.target.value }})}
                                    placeholder="e.g., Sarah Johnson"
                                    data-testid="guest-name-input"
                                />
                            </div>
                            <div>
                                <Label>Email *</Label>
                                <Input
                                    type="email"
                                    value={guestModal.data.email}
                                    onChange={(e) => setGuestModal({ ...guestModal, data: { ...guestModal.data, email: e.target.value }})}
                                    placeholder="sarah@example.com"
                                    data-testid="guest-email-input"
                                />
                            </div>
                            <div>
                                <Label>Phone</Label>
                                <Input
                                    value={guestModal.data.phone || ''}
                                    onChange={(e) => setGuestModal({ ...guestModal, data: { ...guestModal.data, phone: e.target.value }})}
                                    placeholder="07123 456789"
                                />
                            </div>
                            <div>
                                <Label>Relationship</Label>
                                <select
                                    className="w-full border rounded-md p-2 mt-1"
                                    value={guestModal.data.relationship}
                                    onChange={(e) => setGuestModal({ ...guestModal, data: { ...guestModal.data, relationship: e.target.value }})}
                                    data-testid="guest-relationship-select"
                                >
                                    {RELATIONSHIPS.map(rel => (
                                        <option key={rel} value={rel}>{rel}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <Label>Access Level</Label>
                                <select
                                    className="w-full border rounded-md p-2 mt-1"
                                    value={guestModal.data.access_level}
                                    onChange={(e) => setGuestModal({ ...guestModal, data: { ...guestModal.data, access_level: e.target.value }})}
                                    data-testid="guest-access-select"
                                >
                                    {ACCESS_LEVELS.map(level => (
                                        <option key={level.value} value={level.value}>{level.label}</option>
                                    ))}
                                </select>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {ACCESS_LEVELS.find(l => l.value === guestModal.data.access_level)?.description}
                                </p>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setGuestModal({ open: false, data: null })}>
                            Cancel
                        </Button>
                        <Button onClick={handleSaveGuest} className="bg-rose-600 hover:bg-rose-700" data-testid="save-guest-btn">
                            {guestModal.data?.id ? 'Update' : 'Add'} Guest
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Send Invitation Modal */}
            <Dialog open={inviteModal.open} onOpenChange={(open) => !open && setInviteModal({ open: false, data: null })}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Send Invitation</DialogTitle>
                    </DialogHeader>
                    {inviteModal.data && (
                        <div className="space-y-4">
                            {/* Select from existing guests or enter new */}
                            {guests.length > 0 && !inviteModal.data.guest_id && (
                                <div>
                                    <Label>Select a Guest (optional)</Label>
                                    <select
                                        className="w-full border rounded-md p-2 mt-1"
                                        value={inviteModal.data.guest_id || ''}
                                        onChange={(e) => {
                                            const guest = guests.find(g => g.id === e.target.value);
                                            if (guest) {
                                                setInviteModal({
                                                    ...inviteModal,
                                                    data: {
                                                        ...inviteModal.data,
                                                        guest_id: guest.id,
                                                        guest_name: guest.name,
                                                        guest_email: guest.email
                                                    }
                                                });
                                            }
                                        }}
                                    >
                                        <option value="">-- Enter new guest --</option>
                                        {guests.map(g => (
                                            <option key={g.id} value={g.id}>{g.name} ({g.relationship})</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            
                            <div>
                                <Label>Guest Name *</Label>
                                <Input
                                    value={inviteModal.data.guest_name}
                                    onChange={(e) => setInviteModal({ ...inviteModal, data: { ...inviteModal.data, guest_name: e.target.value }})}
                                    placeholder="e.g., Sarah Johnson"
                                    data-testid="invite-name-input"
                                />
                            </div>
                            <div>
                                <Label>Guest Email</Label>
                                <Input
                                    type="email"
                                    value={inviteModal.data.guest_email || ''}
                                    onChange={(e) => setInviteModal({ ...inviteModal, data: { ...inviteModal.data, guest_email: e.target.value }})}
                                    placeholder="sarah@example.com"
                                />
                            </div>
                            <div>
                                <Label>Invitation Type</Label>
                                <div className="grid grid-cols-3 gap-2 mt-2">
                                    {INVITATION_TYPES.map((type) => (
                                        <button
                                            key={type.value}
                                            onClick={() => setInviteModal({ ...inviteModal, data: { ...inviteModal.data, event_type: type.value }})}
                                            className={`p-3 rounded-lg border text-center transition-colors ${
                                                inviteModal.data.event_type === type.value
                                                    ? 'border-rose-500 bg-rose-50'
                                                    : 'border-stone-200 hover:bg-stone-50'
                                            }`}
                                        >
                                            <type.icon className={`w-5 h-5 mx-auto mb-1 ${
                                                inviteModal.data.event_type === type.value ? 'text-rose-600' : 'text-stone-500'
                                            }`} />
                                            <span className={`text-xs font-medium ${
                                                inviteModal.data.event_type === type.value ? 'text-rose-700' : 'text-stone-600'
                                            }`}>
                                                {type.label}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <Label>Date *</Label>
                                <Input
                                    type="date"
                                    value={inviteModal.data.date}
                                    onChange={(e) => setInviteModal({ ...inviteModal, data: { ...inviteModal.data, date: e.target.value }})}
                                    data-testid="invite-date-input"
                                />
                            </div>
                            <div>
                                <Label>Notes</Label>
                                <Textarea
                                    value={inviteModal.data.notes || ''}
                                    onChange={(e) => setInviteModal({ ...inviteModal, data: { ...inviteModal.data, notes: e.target.value }})}
                                    placeholder="Any special notes or requests..."
                                    rows={2}
                                />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setInviteModal({ open: false, data: null })}>
                            Cancel
                        </Button>
                        <Button onClick={handleSendInvitation} className="bg-rose-600 hover:bg-rose-700" data-testid="send-invite-submit-btn">
                            <Send className="w-4 h-4 mr-2" />
                            Send Invitation
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={deleteConfirm.open} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, guest: null })}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Remove Guest</DialogTitle>
                    </DialogHeader>
                    <p className="text-stone-600">
                        Are you sure you want to remove <strong>{deleteConfirm.guest?.name}</strong> from your guest list?
                    </p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteConfirm({ open: false, guest: null })}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleDeleteGuest} data-testid="confirm-delete-guest-btn">
                            Remove Guest
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Share Portal Modal */}
            <Dialog open={shareModal.open} onOpenChange={(open) => !open && setShareModal({ open: false, guest: null })}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Share Family Portal</DialogTitle>
                        <DialogDescription>
                            Give {shareModal.guest?.name} access to view your activities
                        </DialogDescription>
                    </DialogHeader>
                    {shareModal.guest && (
                        <div className="space-y-4">
                            <div className="bg-stone-50 rounded-xl p-4">
                                <Label className="text-xs text-stone-500 uppercase tracking-wide">Portal Link</Label>
                                <div className="flex items-center gap-2 mt-2">
                                    <Input
                                        readOnly
                                        value={getPortalLink(shareModal.guest.access_code)}
                                        className="text-sm font-mono"
                                    />
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => copyPortalLink(shareModal.guest)}
                                        data-testid="copy-portal-link-btn"
                                    >
                                        <Copy className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                            
                            <div className="border-t pt-4">
                                <p className="text-sm text-stone-600 mb-3">
                                    {shareModal.guest.email 
                                        ? `Send portal access to ${shareModal.guest.email}?`
                                        : 'No email address on file. Copy the link above to share manually.'
                                    }
                                </p>
                                {shareModal.guest.email && (
                                    <Button
                                        onClick={() => sendPortalEmail(shareModal.guest)}
                                        disabled={sendingEmail}
                                        className="w-full bg-rose-600 hover:bg-rose-700"
                                        data-testid="send-portal-email-btn"
                                    >
                                        {sendingEmail ? (
                                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                        ) : (
                                            <Mail className="w-4 h-4 mr-2" />
                                        )}
                                        Send Email with Portal Link
                                    </Button>
                                )}
                            </div>

                            <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                                <p className="text-xs text-amber-800">
                                    <strong>Access Level:</strong> {ACCESS_LEVELS.find(l => l.value === shareModal.guest.access_level)?.label}
                                    <br />
                                    {ACCESS_LEVELS.find(l => l.value === shareModal.guest.access_level)?.description}
                                </p>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShareModal({ open: false, guest: null })}>
                            Done
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default FamilyGuestsPage;
